import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function useTableView(entity, defaultColumns = []) {
    const { getAuthHeader } = useAuth();
    const [views, setViews] = useState([]);
    const [activeViewId, setActiveViewId] = useState(null);
    const [visibleColumnKeys, setVisibleColumnKeys] = useState(defaultColumns);
    const [columnSizing, setColumnSizing] = useState({});
    const [sorting, setSorting] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const saveTimer = useRef(null);

    // Load views on mount
    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${API}/api/table-views`, {
                    headers: getAuthHeader(),
                    params: { entity },
                });
                const data = res.data || [];
                setViews(data);

                // Use default view if exists, otherwise use first view
                const defaultView = data.find(v => v.is_default) || data[0];
                if (defaultView) {
                    applyView(defaultView);
                } else {
                    // No views — use default columns
                    setVisibleColumnKeys(defaultColumns);
                }
            } catch { /* keep defaults */ }
            finally { setLoaded(true); }
        };
        load();
    }, [entity, getAuthHeader]); // eslint-disable-line

    const applyView = (view) => {
        setActiveViewId(view.view_id);
        const cols = (view.columns || [])
            .filter(c => c.visible !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(c => c.field_key);
        setVisibleColumnKeys(cols.length > 0 ? cols : defaultColumns);

        // Apply column widths
        const sizing = {};
        (view.columns || []).forEach(c => {
            if (c.width) sizing[c.field_key] = c.width;
        });
        setColumnSizing(sizing);

        // Apply sorting
        if (view.sort_by) {
            setSorting([{ id: view.sort_by, desc: view.sort_direction === 'desc' }]);
        } else {
            setSorting([]);
        }
        setIsDirty(false);
    };

    const switchView = useCallback((viewId) => {
        const view = views.find(v => v.view_id === viewId);
        if (view) applyView(view);
    }, [views]); // eslint-disable-line

    const buildColumnsPayload = useCallback(() => {
        return visibleColumnKeys.map((key, idx) => ({
            field_key: key,
            visible: true,
            order: idx,
            width: columnSizing[key] || null,
        }));
    }, [visibleColumnKeys, columnSizing]);

    const saveView = useCallback(async () => {
        if (!activeViewId) return;
        try {
            const payload = {
                columns: buildColumnsPayload(),
                sort_by: sorting[0]?.id || null,
                sort_direction: sorting[0]?.desc ? 'desc' : 'asc',
            };
            await axios.put(`${API}/api/table-views/${activeViewId}`, payload, { headers: getAuthHeader() });
            setIsDirty(false);
        } catch { /* ignore */ }
    }, [activeViewId, buildColumnsPayload, sorting, getAuthHeader]);

    const saveAsView = useCallback(async (name) => {
        try {
            const payload = {
                name,
                entity,
                columns: buildColumnsPayload(),
                sort_by: sorting[0]?.id || null,
                sort_direction: sorting[0]?.desc ? 'desc' : 'asc',
            };
            const res = await axios.post(`${API}/api/table-views`, payload, { headers: getAuthHeader() });
            setViews(prev => [res.data, ...prev]);
            setActiveViewId(res.data.view_id);
            setIsDirty(false);
        } catch { /* ignore */ }
    }, [entity, buildColumnsPayload, sorting, getAuthHeader]);

    const deleteView = useCallback(async (viewId) => {
        try {
            await axios.delete(`${API}/api/table-views/${viewId}`, { headers: getAuthHeader() });
            setViews(prev => prev.filter(v => v.view_id !== viewId));
            if (activeViewId === viewId) {
                setActiveViewId(null);
                setVisibleColumnKeys(defaultColumns);
            }
        } catch { /* ignore */ }
    }, [activeViewId, defaultColumns, getAuthHeader]);

    const pushToOrg = useCallback(async (viewId) => {
        try {
            await axios.post(`${API}/api/table-views/${viewId}/push`, {}, { headers: getAuthHeader() });
            setViews(prev => prev.map(v => v.view_id === viewId ? { ...v, scope: 'org', is_template: true } : v));
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    // Mark dirty on column changes
    const updateColumns = useCallback((newKeys) => {
        setVisibleColumnKeys(newKeys);
        setIsDirty(true);
    }, []);

    const addColumn = useCallback((key) => {
        setVisibleColumnKeys(prev => [...prev, key]);
        setIsDirty(true);
    }, []);

    const removeColumn = useCallback((key) => {
        setVisibleColumnKeys(prev => prev.filter(k => k !== key));
        setIsDirty(true);
    }, []);

    const handleSortingChange = useCallback((updater) => {
        setSorting(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            setIsDirty(true);
            return next;
        });
    }, []);

    const handleColumnSizingChange = useCallback((updater) => {
        setColumnSizing(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            setIsDirty(true);
            return next;
        });
    }, []);

    return {
        views,
        activeViewId,
        visibleColumnKeys,
        columnSizing,
        sorting,
        isDirty,
        loaded,
        switchView,
        saveView,
        saveAsView,
        deleteView,
        pushToOrg,
        addColumn,
        removeColumn,
        updateColumns,
        setSorting: handleSortingChange,
        setColumnSizing: handleColumnSizingChange,
    };
}
